import { FIELD_CTX } from "../data/labStore";
import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, GitBranch, Plus, Trash2 } from "lucide-react";
import { getFlowMessage } from "../data/flowMessages";
import LabCommentSuggest from "./LabCommentSuggest";
import {
  addBranch,
  addReply,
  addStep,
  findFlow,
  flowsByAudience,
  linkedIds,
  moveStep,
  removeNode,
  removeReply,
  resetFlow,
  spineNodes,
  updateNode,
  updateReply,
} from "../data/flowBlueprint";

function scriptedCopy(messageKey, revisions, studentName) {
  try {
    const meta = getFlowMessage(messageKey);
    const ctx = { ...FIELD_CTX, subject: "cette matière", minutes: 25, item: "marge" };
    const originalMessage = meta.text(ctx);
    const originalPrompt = meta.prompt;
    return {
      prompt:
        typeof revisions.promptOverrides?.[messageKey] === "string"
          ? revisions.promptOverrides[messageKey]
          : originalPrompt,
      message:
        typeof revisions.overrides?.[messageKey] === "string"
          ? revisions.overrides[messageKey]
          : originalMessage,
    };
  } catch {
    return { prompt: "", message: "" };
  }
}

function nodeCopy(node, revisions, studentName) {
  if (!node.messageKey) return { prompt: node.prompt, message: node.message };
  return scriptedCopy(node.messageKey, revisions, studentName);
}

function NodeEditor({
  node,
  flow,
  revisions,
  studentName,
  onChange,
  onPlay,
  onOpenSimulation,
  onSaveScriptPrompt,
  onSaveScriptMessage,
  onSaveComment,
}) {
  const copy = nodeCopy(node, revisions, studentName);
  const [promptDraft, setPromptDraft] = useState(copy.prompt);
  const [messageDraft, setMessageDraft] = useState(copy.message);
  const [relanceDraft, setRelanceDraft] = useState(node.relance || { message: "", when: "" });
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setPromptDraft(copy.prompt);
    setMessageDraft(copy.message);
    setRelanceDraft(node.relance || { message: "", when: "" });
    setNotice("");
    setError("");
  }, [node.id, copy.prompt, copy.message, node.relance]);

  const save = () => {
    setError("");
    setNotice("");
    const notes = [];
    const custom = {};
    if (promptDraft.trim() !== copy.prompt.trim()) {
      if (node.messageKey) {
        const result = onSaveScriptPrompt(node.messageKey, promptDraft);
        if (!result?.ok) {
          setError(result?.reason || "Le prompt n’a pas été enregistré.");
          return;
        }
        if (result.summary) notes.push(result.summary);
      } else {
        custom.prompt = promptDraft.trim();
      }
    }
    if (messageDraft.trim() !== copy.message.trim()) {
      if (!messageDraft.trim()) {
        setError("Le message ne peut pas être vide.");
        return;
      }
      if (node.messageKey) {
        const result = onSaveScriptMessage(node.messageKey, messageDraft);
        if (!result?.ok) {
          setError(result?.reason || "Le message n’a pas été enregistré.");
          return;
        }
        if (result.summary) notes.push(result.summary);
      } else {
        custom.message = messageDraft.trim();
      }
    }
    const previous = node.relance || { message: "", when: "" };
    const nextRelance = {
      message: relanceDraft.message.trim(),
      when: relanceDraft.when.trim(),
    };
    if (nextRelance.message !== previous.message || nextRelance.when !== previous.when) {
      custom.relance = nextRelance;
    }
    if (custom.prompt != null || custom.message != null || custom.relance) {
      onChange(updateNode(onChange.blueprint, flow.id, node.id, custom));
      notes.push("Enregistré pour cette étape.");
    }
    setNotice(notes.filter(Boolean).join(" ") || "Aucun changement à enregistrer.");
  };

  const nameOf = (id) => {
    for (const item of onChange.blueprint.flows) {
      const found = item.nodes.find((node) => node.id === id);
      if (found) return found.title;
    }
    return "Étape retirée";
  };

  const persistPrompt = (text) => {
    setPromptDraft(text);
    if (node.messageKey) return onSaveScriptPrompt(node.messageKey, text);
    onChange(updateNode(onChange.blueprint, flow.id, node.id, { prompt: text.trim() }));
    return { ok: true };
  };

  const persistMessage = (text) => {
    const next = text.trim();
    if (!next) return { ok: false, reason: "Le message ne peut pas être vide." };
    setMessageDraft(next);
    if (node.messageKey) return onSaveScriptMessage(node.messageKey, next);
    onChange(updateNode(onChange.blueprint, flow.id, node.id, { message: next }));
    return { ok: true };
  };

  return (
    <div className="mt-2 rounded-xl border border-[#E5F6EC] bg-white p-2.5">
      <label className="block text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">
        Titre
      </label>
      <input
        value={node.title}
        data-testid={`lab-tree-title-${node.id}`}
        onChange={(event) =>
          onChange(updateNode(onChange.blueprint, flow.id, node.id, { title: event.target.value }))
        }
        className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs outline-none focus:border-[#25D366]"
      />

      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">
          Chemins possibles
        </p>
        <button
          type="button"
          data-testid={`lab-tree-add-reply-${node.id}`}
          onClick={() => onChange(addReply(onChange.blueprint, flow.id, node.id).blueprint)}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#189346]"
        >
          <Plus className="h-3 w-3" />
          Réponse
        </button>
      </div>
      {node.replies.length === 0 && (
        <p className="mt-1 text-[11px] text-[#829C8D]">Aucune réponse. Ce chemin s’arrête après le message.</p>
      )}
      <ul className="mt-1 space-y-2">
        {node.replies.map((reply) => (
          <li key={reply.id} className="rounded-lg bg-[#F7FAF8] p-2">
            <div className="flex items-center gap-1">
              <input
                value={reply.label}
                aria-label="Titre de la réponse"
                data-testid={`lab-tree-reply-${reply.id}`}
                onChange={(event) =>
                  onChange(
                    updateReply(onChange.blueprint, flow.id, node.id, reply.id, { label: event.target.value })
                  )
                }
                className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs outline-none focus:border-[#25D366]"
              />
              <button
                type="button"
                aria-label="Retirer la réponse"
                onClick={() => onChange(removeReply(onChange.blueprint, flow.id, node.id, reply.id))}
                className="rounded-lg p-1 text-[#829C8D] hover:bg-white hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-1 flex items-center gap-1">
              <span className="shrink-0 text-[10px] font-semibold text-[#829C8D]">Vers</span>
              <select
                value={reply.to}
                aria-label="Chemin de la réponse"
                data-testid={`lab-tree-dest-${reply.id}`}
                onChange={(event) =>
                  onChange(
                    updateReply(onChange.blueprint, flow.id, node.id, reply.id, { to: event.target.value })
                  )
                }
                className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs outline-none focus:border-[#25D366]"
              >
                <option value="">Fin du chemin</option>
                {reply.to && !flow.nodes.some((item) => item.id === reply.to) && (
                  <option value={reply.to}>{nameOf(reply.to)}</option>
                )}
                <optgroup label="Étapes">
                  {spineNodes(flow).map((item, index) => (
                    <option key={item.id} value={item.id}>
                      {index + 1}. {item.title}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Chemins">
                  {flow.nodes
                    .filter((item) => item.role === "branch")
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.title}
                      </option>
                    ))}
                </optgroup>
              </select>
              <button
                type="button"
                onClick={() => onChange(addBranch(onChange.blueprint, flow.id, node.id, reply.id).blueprint)}
                className="shrink-0 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] font-semibold text-[#122118]"
              >
                Nouveau
              </button>
            </div>
            <p className="mt-1 text-[10px] text-[#4a5f53]">
              {reply.label || "Réponse"} → {reply.to ? nameOf(reply.to) : "fin"}
            </p>
          </li>
        ))}
      </ul>

      <label className="mt-3 block text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">
        Prompt
      </label>
      <textarea
        value={promptDraft}
        data-testid={`lab-tree-prompt-${node.id}`}
        rows={3}
        onChange={(event) => setPromptDraft(event.target.value)}
        className="mt-1 w-full resize-y rounded-lg border border-gray-200 px-2 py-1.5 text-xs outline-none focus:border-[#25D366]"
      />
      <label className="mt-2 block text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">
        Message
      </label>
      <textarea
        value={messageDraft}
        data-testid={`lab-tree-message-${node.id}`}
        rows={3}
        onChange={(event) => setMessageDraft(event.target.value)}
        className="mt-1 w-full resize-y rounded-lg border border-gray-200 px-2 py-1.5 text-xs outline-none focus:border-[#25D366]"
      />
      <label className="mt-3 block text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">
        Relance
      </label>
      <input
        value={relanceDraft.when}
        data-testid={`lab-tree-relance-when-${node.id}`}
        onChange={(event) => setRelanceDraft((current) => ({ ...current, when: event.target.value }))}
        placeholder="Quand : chaque jour, 1 h avant, 10 min après…"
        className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs outline-none focus:border-[#25D366]"
      />
      <textarea
        value={relanceDraft.message}
        data-testid={`lab-tree-relance-${node.id}`}
        rows={2}
        onChange={(event) => setRelanceDraft((current) => ({ ...current, message: event.target.value }))}
        placeholder="Message de relance. Vide = pas de relance."
        className="mt-1 w-full resize-y rounded-lg border border-gray-200 px-2 py-1.5 text-xs outline-none focus:border-[#25D366]"
      />
      <LabCommentSuggest
        key={node.id}
        idPrefix={`lab-tree-${node.id}`}
        prompt={promptDraft}
        message={messageDraft}
        label={node.title}
        initialComment={(revisions.comments || {})[node.messageKey || node.id] || ""}
        onCommentChange={(text) => onSaveComment?.(node.messageKey || node.id, text)}
        onUsePrompt={persistPrompt}
        onUseMessage={persistMessage}
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          data-testid={`lab-tree-save-${node.id}`}
          onClick={save}
          className="rounded-full bg-[#122118] px-3 py-1.5 text-[11px] font-semibold text-white"
        >
          Enregistrer prompt, message et relance
        </button>
        <button
          type="button"
          data-testid={`lab-tree-play-${node.id}`}
          onClick={() => onPlay(flow.id, node.id)}
          className="rounded-full border border-gray-200 px-3 py-1.5 text-[11px] font-semibold text-[#122118]"
        >
          Voir ce fil
        </button>
        {node.scriptIndex != null && flow.audience !== "parent" && (
          <button
            type="button"
            onClick={() => onOpenSimulation(flow.id, node.scriptIndex)}
            className="rounded-full border border-gray-200 px-3 py-1.5 text-[11px] font-semibold text-[#4a5f53]"
          >
            Simulation riche
          </button>
        )}
      </div>
      {error && <p className="mt-2 text-[11px] text-red-600">{error}</p>}
      {notice && <p className="mt-2 text-[11px] font-semibold text-[#0B8F6A]">{notice}</p>}
    </div>
  );
}

export default function LabFlowTree({
  blueprint,
  flowId,
  audience = "eleve",
  revisions,
  studentName,
  activeNodeId,
  onChange,
  onPlay,
  onOpenSimulation,
  onSaveScriptPrompt,
  onSaveScriptMessage,
  onSaveComment,
  onClose,
}) {
  const visible = flowsByAudience(blueprint, audience);
  const [selectedFlowId, setSelectedFlowId] = useState(flowId);
  const [openId, setOpenId] = useState(null);
  const shownId = visible.some((item) => item.id === selectedFlowId)
    ? selectedFlowId
    : visible[0]?.id ?? flowId;

  useEffect(() => {
    setSelectedFlowId((current) => {
      const list = flowsByAudience(blueprint, audience);
      if (list.some((item) => item.id === flowId)) return flowId;
      if (list.some((item) => item.id === current)) return current;
      return list[0]?.id ?? flowId;
    });
    // The open flow follows the phone. A local choice stays until that flow changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId, audience]);

  const flow = findFlow(blueprint, shownId);
  const steps = spineNodes(flow);
  const linked = linkedIds(flow);
  const loose = flow.nodes.filter((node) => node.role === "branch" && !linked.has(node.id));
  const change = (next) => onChange(next);
  change.blueprint = blueprint;

  const renderNode = (node, depth, seen) => {
    if (seen.has(node.id) || depth > 6) return null;
    const nextSeen = new Set(seen);
    nextSeen.add(node.id);
    const open = openId === node.id;
    const current = node.id === activeNodeId;
    return (
      <li key={node.id} className={depth ? "ml-4 border-l border-[#C6EBD7] pl-3" : ""}>
        <div
          className={`rounded-xl px-2 py-1.5 ${current ? "bg-[#E9FBF0]" : "bg-[#F7FAF8]"}`}
          data-testid={`lab-tree-node-${node.id}`}
        >
          <div className="flex items-start gap-1">
            <button
              type="button"
              onClick={() => setOpenId(open ? null : node.id)}
              className="min-w-0 flex-1 text-left text-xs font-semibold text-[#122118]"
            >
              <span className="mr-1 text-[10px] font-bold text-[#189346]">
                {node.role === "branch" ? "chemin" : `${steps.findIndex((item) => item.id === node.id) + 1}.`}
              </span>
              {node.title}
            </button>
            {node.role === "step" && (
              <>
                <button
                  type="button"
                  aria-label="Monter l’étape"
                  data-testid={`lab-tree-up-${node.id}`}
                  onClick={() => change(moveStep(blueprint, flow.id, node.id, -1))}
                  className="rounded-lg p-1 text-[#829C8D] hover:bg-white"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  aria-label="Descendre l’étape"
                  data-testid={`lab-tree-down-${node.id}`}
                  onClick={() => change(moveStep(blueprint, flow.id, node.id, 1))}
                  className="rounded-lg p-1 text-[#829C8D] hover:bg-white"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
              </>
            )}
            <button
              type="button"
              aria-label="Retirer"
              data-testid={`lab-tree-remove-${node.id}`}
              onClick={() => change(removeNode(blueprint, flow.id, node.id))}
              className="rounded-lg p-1 text-[#829C8D] hover:bg-white hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="mt-1 space-y-0.5">
            {node.replies.map((reply) => {
              const dest = blueprint.flows
                .flatMap((item) => item.nodes)
                .find((item) => item.id === reply.to);
              return (
                <li key={reply.id} className="text-[11px] leading-snug text-[#4a5f53]">
                  <span className="text-[#829C8D]">↳ </span>
                  {reply.label || "Réponse"}
                  <span className="text-[#829C8D]"> → </span>
                  {dest ? dest.title : "fin"}
                </li>
              );
            })}
          </ul>
          {open && (
            <NodeEditor
              node={node}
              flow={flow}
              revisions={revisions}
              studentName={studentName}
              onChange={change}
              onPlay={onPlay}
              onOpenSimulation={onOpenSimulation}
              onSaveScriptPrompt={onSaveScriptPrompt}
              onSaveScriptMessage={onSaveScriptMessage}
              onSaveComment={onSaveComment}
            />
          )}
        </div>
        {node.replies.some((reply) => flow.nodes.some((item) => item.id === reply.to && item.role === "branch")) && (
          <ul className="mt-1 space-y-1">
            {node.replies.map((reply) => {
              const dest = flow.nodes.find((item) => item.id === reply.to && item.role === "branch");
              return dest ? renderNode(dest, depth + 1, nextSeen) : null;
            })}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div
      id="lab-tree-panel"
      data-testid="lab-tree-panel"
      className="absolute right-0 top-full z-20 mt-2 w-[min(36rem,calc(100vw-2rem))] max-h-[74vh] overflow-y-auto rounded-2xl border border-gray-100 bg-white p-3 shadow-xl"
    >
      <div className="flex items-center justify-between gap-2 px-1">
        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">
          <GitBranch className="h-3.5 w-3.5" />
          Arbre des chemins
        </p>
        <button type="button" className="sr-only" onClick={onClose}>
          Fermer
        </button>
      </div>
      <p className="px-1 pb-2 pt-1 text-[11px] leading-snug text-[#4a5f53]">
        {audience === "parent"
          ? "Flows parent numérotés. Titre, réponse, prompt, message et relance. Voir ce fil joue l’étape dans la vue parent."
          : "Titre, réponse, chemin, prompt, message et relance. Ce réglage déterministe reste dans le lab pour l’instant. Voir ce fil joue le message et la relance."}
      </p>
      <label className="block px-1 text-[10px] font-bold uppercase tracking-wider text-[#829C8D]" htmlFor="lab-tree-flow">
        Flow
      </label>
      <select
        id="lab-tree-flow"
        data-testid="lab-tree-flow"
        value={flow.id}
        onChange={(event) => {
          setSelectedFlowId(Number(event.target.value));
          setOpenId(null);
        }}
        className="mt-1 w-full rounded-xl border border-gray-200 px-2 py-1.5 text-xs outline-none focus:border-[#25D366]"
      >
        {visible.map((item) => (
          <option key={item.id} value={item.id}>
            {item.title}
          </option>
        ))}
      </select>

      <ol className="mt-3 space-y-2">
        {steps.map((node) => renderNode(node, 0, new Set()))}
      </ol>

      <div className="mt-3 flex flex-wrap gap-2 px-1">
        <button
          type="button"
          data-testid="lab-tree-add-step"
          onClick={() => {
            const after = steps[steps.length - 1]?.id;
            const result = addStep(blueprint, flow.id, after);
            change(result.blueprint);
            setOpenId(result.nodeId);
          }}
          className="inline-flex items-center gap-1 rounded-full bg-[#122118] px-3 py-1.5 text-[11px] font-semibold text-white"
        >
          <Plus className="h-3 w-3" />
          Ajouter une étape
        </button>
        <button
          type="button"
          data-testid="lab-tree-reset"
          onClick={() => change(resetFlow(blueprint, flow.id))}
          className="rounded-full border border-gray-200 px-3 py-1.5 text-[11px] font-semibold text-[#4a5f53]"
        >
          Arbre d’origine
        </button>
      </div>

      {loose.length > 0 && (
        <div className="mt-3">
          <p className="px-1 text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">
            Chemins non reliés
          </p>
          <ul className="mt-1 space-y-1">
            {loose.map((node) => renderNode(node, 0, new Set()))}
          </ul>
        </div>
      )}
    </div>
  );
}
